import { civilToday, dayKey, zonedMidnight } from '@/components/fleet/period';
import { readEnumParam, readUuidParam } from '@/lib/list-view/use-list-params';

export const HISTORY_RANGES = ['24h', '7d', '30d', 'custom'] as const;

export type HistoryRange = (typeof HISTORY_RANGES)[number];

export type HistorySelection = {
  readonly machineId: string | null;
  readonly range: HistoryRange;
  readonly from: string | null;
  readonly to: string | null;
};

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const DEFAULT_CUSTOM_DAYS = 7;

const PRESET_SPAN_MS: Readonly<Record<Exclude<HistoryRange, 'custom'>, number>> = {
  '24h': DAY_MS,
  '7d': 7 * DAY_MS,
  '30d': 30 * DAY_MS,
};

/** Strict `YYYY-MM-DD` that names a real calendar day (`2026-02-31` is not one). */
export function isDayKey(value: string | null): value is string {
  return value !== null && DAY_KEY.test(value) && dayKey(new Date(`${value}T00:00:00Z`)) === value;
}

export function todayKey(now: Date): string {
  return dayKey(civilToday(now));
}

function shiftDays(key: string, days: number): string {
  return dayKey(new Date(Date.parse(`${key}T00:00:00Z`) + days * DAY_MS));
}

/** A custom range's days: a missing or unreadable day gets its default, neither passes today, and the start day never passes the end day. */
export function customDays(
  from: string | null,
  to: string | null,
  today: string,
): { readonly from: string; readonly to: string } {
  const earliest = (key: string) => (key > today ? today : key);
  const end = earliest(isDayKey(to) ? to : today);
  const start = earliest(isDayKey(from) ? from : shiftDays(today, -DEFAULT_CUSTOM_DAYS));

  return start > end ? { from: start, to: start } : { from: start, to: end };
}

export function readSelection(params: URLSearchParams, today: string): HistorySelection {
  const range = readEnumParam(params, 'range', HISTORY_RANGES) ?? '24h';
  const days = range === 'custom' ? customDays(params.get('from'), params.get('to'), today) : null;

  return { machineId: readUuidParam(params, 'machine'), range, from: days?.from ?? null, to: days?.to ?? null };
}

/** The readings window: a preset ends now; a custom range runs Berlin midnight to Berlin midnight, Bis included. */
export function rangeWindow(
  range: HistoryRange,
  from: string | null,
  to: string | null,
  now: Date,
): { readonly from: Date; readonly to: Date } {
  if (range !== 'custom') return { from: new Date(now.getTime() - PRESET_SPAN_MS[range]), to: now };

  const days = customDays(from, to, todayKey(now));

  // Days are added on the civil date, never as 24 h on an instant, which is wrong across a DST change.
  return {
    from: zonedMidnight(new Date(`${days.from}T00:00:00Z`)),
    to: zonedMidnight(new Date(`${shiftDays(days.to, 1)}T00:00:00Z`)),
  };
}
